import { maskVendor } from "@/shared/lib/vendorMask";
import { stripSystemPromptLines } from "@/shared/lib/customerPromptLines";
import { useEffect, useRef, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantAdminPlus, ROLES } from "@/shared/lib/rbac";
import { Link, useNavigate, useParams } from "react-router-dom";

import { dhScriptFromVoiceovers } from "../model/dhHandoff";
import { partialResumeNotice } from "../model/composeFailNotice";
import { tallyChunks } from "../model/chunkTally";
import { degradeSummary, dropDegradedTags, finalDegradeNotice } from "../model/videoDegradeSummary";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Textarea } from "@/shared/ui/textarea";
import { AutoGrowTextarea } from "@/shared/ui/auto-grow-textarea";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useToast } from "@/shared/hooks/use-toast";
import { useFrozenAssetUrl } from "../model/useFrozenAssetUrl";
import { useVideoTaskPolling } from "../model/useVideoTaskPolling";
import { videoStyleLabel } from "../ui/components/videoStyleOptions";
import { mediaDownloadFilename, triggerDownload } from "@/shared/lib/download";
import { NominateToKbButton } from "@/entities/knowledge/ui/NominateToKbButton";
import { DH_LINE_ROUTE } from "@/entities/dh-profile/model/dhLines";
import { isLandscape, ratioToCss } from "@/shared/lib/video/aspect";
import { cn } from "@/shared/lib/utils";
import {
  EMOTION_LABEL,
  FONT_LABEL,
  TRANSITION_LABEL,
  VOICE_TONE_LABEL,
  generationModeLabel,
  ratioLabel,
  videoProviderLabel,
} from "@/shared/lib/video/labels";
// isTaskActive:在途判定单一真相源。此前本页自己写了份 ACTIVE Set 且**漏了 RUNNING**
// (只列 PENDING + 一串 rebuild 后端不产的 legacy 态)→ 任务被 worker 认领后本页所有
// 「是否在途」判定全部误判成终态。见 taskStatus.isTaskActive 的注释。
import {
  taskStatusLabel,
  isTaskActive,
  taskStatusBorderedBadgeClass,
} from "@/shared/lib/taskStatus";
import { Download, Loader2, Sparkles, Target } from "lucide-react";
import {
  EMPTY_SHOT_EDIT_FORM,
  shotEditForm,
  shotEditPayload,
  type ShotEditForm,
} from "../model/shotEditForm";
import { changedShots, chunkEditDraft, type ChunkEditDraft } from "../model/chunkEdit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { VideoChunkTextEditor } from "./components/VideoChunkTextEditor";
import { videoService, type VideoComposeOptions } from "../api/videoService";
import { useComposeEditor } from "./components/VideoComposePanel";
import { VideoChunkGrid } from "./components/VideoChunkGrid";
import { pickBaseImage, summarizeComposed } from "../model/composePanel";
import { textOverlayOf, textOverlayText, toneRegisterLabel } from "../model/toneRegister";
import { errorText } from "@/shared/lib/apiErrors";

// 这里原本是 { ...TASK_STATUS_LABEL, RENDERING: "AI 渲染中(约 1-5 分钟)" } —— 后端不产 RENDERING,
// 那条覆盖从来没生效过。2026-07-16 删掉,直接用共用标签(行为不变)。

/** 安全取 requestPayload(原始 VideoTaskRequest)里的字段。 */
function rp(payload: Record<string, unknown> | undefined, key: string): unknown {
  return payload ? payload[key] : undefined;
}
function strArr(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}
function numArr(v: unknown): number[] {
  return Array.isArray(v) ? v.map((x) => Number(x)) : [];
}

export default function VideoTaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const taskId = id ? Number(id) : null;
  const { task, error, refresh } = useVideoTaskPolling(taskId);
  const confirm = useConfirm();
  const { toast } = useToast();
  // 按角色分流积分明细入口(ProtectedRoute 角色矩阵,2026-09-03 修 0902 遗留的过时点):
  // TENANT_ADMIN+ 走「我的套餐→积分明细」/my-billing;MANAGER/STAFF 走 0902 新上的自查页
  // /my-credits(appSidebarNav.ts:my_credits 同口径,roles=[TENANT_STAFF,TENANT_MANAGER])。
  // 都没有(MEMBER 等此页本不可达的角色)→ billingLink=null,保留纯文字不给链接
  // (给一个必然 403 的链接比不给更糟,见下方 0819 注)。
  const { effectiveRole } = useAuth();
  const canSeeBilling = isTenantAdminPlus(effectiveRole);
  const canSeeMyCredits =
    effectiveRole === ROLES.TENANT_MANAGER || effectiveRole === ROLES.TENANT_STAFF;
  const billingLink = canSeeBilling
    ? { to: "/my-billing", label: "查看积分流水" }
    : canSeeMyCredits
      ? { to: "/my-credits", label: "查看我的积分" }
      : null;
  // 重新合成跨越异步渲染(点击→RENDERING→SUCCESS):用 ref 记住「这次终态前提交过 recompose」,
  // 终态回来时弹一次完成/失败提示(否则用户点完只看到旧片不动,没有反馈)。
  const recomposePendingRef = useRef(false);
  const lastFinalIdRef = useRef<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [regenSeq, setRegenSeq] = useState<number | null>(null);
  const [composing, setComposing] = useState(false);
  // 单镜编辑(Step2a):editSeq=正在编辑的镜号;editForm=三文本字段草稿;savingEdit=保存中。
  const [editSeq, setEditSeq] = useState<number | null>(null);
  /** VID-D:失败段「改文案再重渲」草稿(null=面板没开)。 */
  const [chunkDraft, setChunkDraft] = useState<ChunkEditDraft | null>(null);
  const [savingChunk, setSavingChunk] = useState(false);
  const [editForm, setEditForm] = useState<ShotEditForm>(EMPTY_SHOT_EDIT_FORM);
  /** 打开编辑框那一刻的原文 —— 用来「只发真改过的字段」(全量发会把空字段写成空串)。 */
  const [editOrigin, setEditOrigin] = useState<ShotEditForm>(EMPTY_SHOT_EDIT_FORM);
  const [savingEdit, setSavingEdit] = useState(false);
  // 轮询每次重签 URL → <video src> 变 → 播放重启。冻结逻辑见 useFrozenAssetUrl 的头注:
  // 它原来是这里的一个闭包,VideoChunkGrid 复用不到,于是分段卡片漏了防护(0923 真机:
  // 多镜任务在途时点分段播放,播一秒就停)。抽成 hook 后两边同一份。
  const stableUrl = useFrozenAssetUrl();

  // 重渲/合成提交后任务一旦进入渲染态,清掉乐观的 regenSeq —— 之后各镜显示由任务状态驱动。
  const taskStatus = task?.status;
  useEffect(() => {
    if (taskStatus && isTaskActive(taskStatus)) setRegenSeq(null);
  }, [taskStatus]);

  // recompose 完成反馈:成片重出片即视为完成,弹一次提示 + 播放器自动换新片。
  // ★FINAL id 不变(后端覆盖同一行),只 generatedAt 变,故签名带 generatedAt;不依赖观测
  // RENDERING 中间态(快速重合成可能一拍就过、status 不变但 generatedAt 已换)。
  const successFinal = task?.assets?.find((a) => a.kind === "FINAL" && a.status === "SUCCESS");
  const successFinalSig = successFinal
    ? `${successFinal.id}:${successFinal.generatedAt ?? ""}`
    : null;
  useEffect(() => {
    if (!recomposePendingRef.current) {
      lastFinalIdRef.current = successFinalSig;
      return;
    }
    if (taskStatus && isTaskActive(taskStatus)) return; // 仍在渲染
    if (taskStatus === "FAILED" || taskStatus === "BLOCKED") {
      recomposePendingRef.current = false;
      toast({ title: "重新合成失败", description: "请稍后重试", variant: "destructive" });
      return;
    }
    if (successFinalSig != null && successFinalSig !== lastFinalIdRef.current) {
      recomposePendingRef.current = false;
      lastFinalIdRef.current = successFinalSig;
      toast({ title: "重新合成完成", description: "新成片已更新,可直接下载" });
    }
  }, [successFinalSig, taskStatus, toast]);

  const onCancel = async () => {
    if (!taskId) return;
    setActionError(null);
    try {
      await videoService.cancelTask(taskId);
    } catch (e: unknown) {
      setActionError(errorText(e));
    }
  };

  /**
   * 失败后「回去改稿」落到哪(见按钮处头注)。草稿优先 —— 它是整份表单;
   * 没草稿退而求其次带文案,两个都没有就不给按钮(不做点了没反应的入口)。
   */
  const reviseTarget = useMemo(() => {
    if (!task) return null;
    const draftId = (task.sourceRef as { draftId?: number } | undefined)?.draftId;
    if (draftId) {
      return {
        label: "回去改稿再出片",
        go: () => navigate(`/ai-video/create?draftId=${draftId}`),
      };
    }
    const userInput = (task.requestPayload as { userInput?: string } | undefined)?.userInput;
    if (userInput && userInput.trim()) {
      return {
        label: "把文案带回创作页改",
        go: () => navigate("/ai-video/create", { state: { prefillPrompt: userInput } }),
      };
    }
    return null;
  }, [task, navigate]);

  const onRecreate = () => {
    if (!task) return;
    const ref = task.sourceRef as
      | { fromPosterTaskId?: number; fromPosterAssetSeq?: number }
      | undefined;
    if (ref?.fromPosterTaskId) {
      const seq = ref.fromPosterAssetSeq ? `&assetSeq=${ref.fromPosterAssetSeq}` : "";
      navigate(`/ai-video/create?fromPoster=${ref.fromPosterTaskId}${seq}`);
    } else {
      navigate("/ai-video/create");
    }
  };

  const onRetry = async () => {
    if (!taskId) return;
    setActionError(null);
    try {
      const { taskId: newId } = await videoService.retryTask(taskId);
      navigate(`/ai-video/tasks/${newId}`);
    } catch (e: unknown) {
      setActionError(errorText(e));
    }
  };

  const onRegenShot = async (seq: number) => {
    if (!taskId) return;
    setActionError(null);
    setRegenSeq(seq);
    try {
      await videoService.regenerateShot(taskId, seq);
      // 任务此刻已翻 RENDERING,但当前页是从终态进来的、轮询早停了。必须 refresh()
      // 重新拉一次(拿到 RENDERING)让轮询 effect 重启,否则界面冻在旧数据要手刷。
      refresh();
      // 成功后**不**清 regenSeq:任务即将翻 RENDERING,这段窗口靠它显示「重渲中」并抑制旧
      // 失败态(治"点重渲后报错还在");待任务进入渲染态由下方 effect 清掉,之后由状态驱动。
    } catch (e: unknown) {
      setActionError(errorText(e));
      setRegenSeq(null);
    }
  };

  const onComposeFinal = async () => {
    if (!taskId) return;
    setActionError(null);
    setComposing(true);
    try {
      await videoService.composeFinal(taskId);
      // 同 onRegenShot:任务翻 RENDERING,但从终态进来的页轮询已停,必须 refresh() 重启。
      refresh();
    } catch (e: unknown) {
      setActionError(errorText(e));
    } finally {
      setComposing(false);
    }
  };

  const onRegenChunk = async (seq: number) => {
    if (!taskId) return;
    setActionError(null);
    setRegenSeq(seq);
    try {
      await videoService.regenerateChunk(taskId, seq);
      // 任务翻 RENDERING;从终态进来的页轮询已停,refresh() 重启它。重渲态由 regenSeq 乐观显示,进渲染态由 effect 清。
      refresh();
    } catch (e: unknown) {
      setActionError(errorText(e));
      setRegenSeq(null);
    }
  };

  const openChunkEdit = (seq: number) => {
    const chunk = (task?.assets ?? []).find((a) => a.kind === "CHUNK" && a.seq === seq);
    if (!chunk) return;
    setActionError(null);
    setChunkDraft(
      chunkEditDraft(chunk, { panelScenes, panelMotions, voiceovers, negativePrompts })
    );
  };

  const patchChunkShot = (shotSeq: number, patch: Partial<ShotEditForm>) =>
    setChunkDraft((d) =>
      d == null
        ? d
        : {
            ...d,
            shots: d.shots.map((sh) =>
              sh.shotSeq === shotSeq ? { ...sh, form: { ...sh.form, ...patch } } : sh
            ),
          }
    );

  /**
   * 存这一段改过的镜;rerender=true 时接着重渲本段。
   *
   * ★顺序与失败处理是这里的全部要点:**任何一镜没存住就不许往下重渲** —— 否则用户花钱
   * 重渲的是**旧文案**,画面照样不对,钱白花,而且界面上看不出哪一步断了。
   * (后端的 regenerateChunk 收不下 edits,改文案只能先 editShot,这一步不是可选的。)
   */
  const onSaveChunkEdit = async (rerender: boolean) => {
    if (!taskId || chunkDraft == null) return;
    const changed = changedShots(chunkDraft);
    if (changed.length === 0) return;
    setActionError(null);
    setSavingChunk(true);
    try {
      for (const sh of changed) {
        await videoService.editShot(taskId, sh.shotSeq, shotEditPayload(sh.form, sh.origin));
      }
      if (rerender) {
        setRegenSeq(chunkDraft.seq);
        await videoService.regenerateChunk(taskId, chunkDraft.seq);
      }
      setChunkDraft(null);
      refresh();
    } catch (e: unknown) {
      // 存到一半断了:面板**留着**(改的字还在,重来一次不用重打),并说清已经存了几镜
      setRegenSeq(null);
      setActionError(
        `${errorText(e)}${rerender ? " —— 文案未全部保存,本段没有重渲,没有产生新的积分消耗。" : ""}`
      );
    } finally {
      setSavingChunk(false);
    }
  };

  const onResume = async () => {
    if (!taskId) return;
    setActionError(null);
    setComposing(true);
    try {
      await videoService.resume(taskId);
      refresh(); // 续渲 → 任务翻 RENDERING;轮询重启。
    } catch (e: unknown) {
      setActionError(errorText(e));
    } finally {
      setComposing(false);
    }
  };

  // 成片编辑「应用并重新合成」:同 onComposeFinal,但带 VideoComposeOptions(声音/字幕/BGM/overlay)。
  const onApplyCompose = async (options: VideoComposeOptions) => {
    if (!taskId) return;
    setActionError(null);
    setComposing(true);
    recomposePendingRef.current = true;
    try {
      await videoService.composeFinal(taskId, options);
      refresh();
    } catch (e: unknown) {
      recomposePendingRef.current = false;
      setActionError(errorText(e));
    } finally {
      setComposing(false);
    }
  };

  /**
   * 打开某镜编辑框,用**当前镜的原文**预填。
   * ★预填源必须与写入目标同一字段:此前框子标着「画面 / 运动描述」、预填 panelMotions(动作)、
   *   后端却写 panelScenes(画面)—— 用户一个字没动点保存,画面描述就被动作顶掉,还不报错。
   */
  const openEdit = (seq: number) => {
    const form = shotEditForm({ panelScenes, panelMotions, voiceovers, negativePrompts }, seq);
    setEditForm(form);
    setEditOrigin(form);
    setEditSeq(seq);
  };

  const onSaveEdit = async (rerender: boolean) => {
    if (!taskId || editSeq == null) return;
    setActionError(null);
    setSavingEdit(true);
    const edits = shotEditPayload(editForm, editOrigin);
    try {
      if (rerender) {
        await videoService.regenerateShot(taskId, editSeq, edits);
        refresh(); // 重渲 → 任务翻 RENDERING;从终态进来的页轮询已停,refresh() 重启它。
      } else {
        await videoService.editShot(taskId, editSeq, edits);
        refresh(); // 仅保存:任务终态轮询停了,手动重取让编辑生效到界面
      }
      setEditSeq(null);
    } catch (e: unknown) {
      setActionError(errorText(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const onDeleteShot = async (seq: number) => {
    if (!taskId) return;
    const ok = await confirm({
      title: `删除第 ${seq} 镜?`,
      description:
        "这一镜的画面 / 运动 / 口播 / 分镜数据会一起删除,后续镜号顺延,成片作废需重新合成。多镜至少保留 2 镜。",
      confirmText: "删除本镜",
      variant: "destructive",
    });
    if (!ok) return;
    setActionError(null);
    try {
      await videoService.deleteShot(taskId, seq);
      refresh(); // 任务已终态、轮询停了,手动重取:分镜数减一,后续镜顺延,成片标过期
    } catch (e: unknown) {
      setActionError(errorText(e));
    }
  };

  const onDownload = async (assetId: number, seq?: number, label?: string) => {
    setActionError(null);
    setDownloading(true);
    try {
      const { url } = await videoService.getDownloadUrl(assetId);
      const suffix = label ? `-${label}` : seq !== undefined && seq > 0 ? `-镜${seq}` : "";
      // 1004 F23 #9:与海报下载同一套文件名规则(作品名安全化,空白兜底)
      const fileName = mediaDownloadFilename(
        task?.name ? `${task.name}${suffix}` : null,
        `video-${assetId}${suffix}`,
        "mp4"
      );
      // Coup-3 (2026-05-30) — 统一走 lib/download 的 triggerDownload(跟海报同一个 helper),
      // 不再各页手搓 anchor。签名 URL 已带 Content-Disposition: attachment(MinIO 强制下载文件名)。
      triggerDownload(url, fileName);
    } catch (e: unknown) {
      setActionError(errorText(e));
    } finally {
      setDownloading(false);
    }
  };

  // ★以下派生值必须算在「加载中/出错」早返回**之前** —— useComposeEditor 要用它们,而 hook
  //   一旦排在早返回之后,首帧(task 还没到)与次帧(task 到了)的 hook 数量就不一样,
  //   React 直接抛「Rendered more hooks than during the previous render」整页白屏。
  //   所以这里一律按 task 可空写;真正用到它们的渲染分支都在早返回之后,值一定齐。
  const isActive = task ? isTaskActive(task.status) : false;

  // 多镜判定 + 每镜数据(来自原始 requestPayload)。
  // ★契约 v2:基准数组 = panelScenes(panelPrompts 已删)。用退休字段判镜数 = 新任务恒 0 镜
  // → isMultiShot 恒 false → 整个多镜区(段网格 / 每镜参数)在详情页直接消失。
  const payload = task?.requestPayload;
  const panelScenes = strArr(rp(payload, "panelScenes"));
  const panelMotions = strArr(rp(payload, "panelMotions"));
  const voiceovers = strArr(rp(payload, "voiceovers"));
  // BP-02:逐镜口播拼成整段;全空 → null → 不渲染「送去数字人」
  const dhScript = dhScriptFromVoiceovers(voiceovers);
  const panelDurations = numArr(rp(payload, "panelDurations"));
  const negativePrompts = strArr(rp(payload, "negativePrompts"));
  // 每镜首帧图(gpt-image-2 出的,创建时签 24h)。失败/渲染中的镜没视频时拿它当预览,比黑框观感好。
  const firstFrameUrls = strArr(rp(payload, "panelFirstFrameUrls"));
  const isMultiShot = task?.type === "PROMO" && panelScenes.length >= 2;
  const shotCount = panelScenes.length;

  // 成片:只认真正合成的 FINAL。**多镜不再 fallback assets[0]**(否则部分失败/渲染中会把镜1
  // 冒充成成片,既藏错误又藏进度)。单镜任务没有独立 FINAL,首个资产即成片。
  const realFinal = task?.assets?.find((a) => a.kind === "FINAL") ?? null;
  const finalAsset =
    task?.type === "DRAMA" || isMultiShot ? realFinal : (realFinal ?? task?.assets?.[0] ?? null);

  // ── VID-D:成片编辑的画布与控件分居中/左两栏,状态用同一个 hook 持有(见 useComposeEditor
  //    的注释:挂进渲染子树会让整页正文依赖它)。baseImageUrl 用 ?? "" 兜底 —— 画布只在
  //    editable 为真时才渲染,那时 pickBaseImage 一定非空。
  const composeEditor = useComposeEditor({
    task,
    finalAsset,
    baseImageUrl: pickBaseImage(task, finalAsset) ?? "",
    applying: composing || isActive,
    onApply: onApplyCompose,
  });
  /**
   * 有可调的东西吗:成片就绪 + 有底图 + **后端受理重新合成**。否则三列退成「画面 + 右栏」两列。
   *
   * ★VID-I2V-LOGO-1(0901 用户实报「图生视频没有加 logo 的地方」):这里原先钉的是
   *   `isMultiShot`(type===PROMO && panelScenes>=2),而「让这张图动起来」发的是
   *   `panelScenes:[""]` **恰好 1 项** ⇒ 闸恒假 ⇒ 「调版式」与左栏「成片编辑」整块不渲染。
   *   编排片与图生视频**本来就是同一个详情页**,差别全出在这一行。修法:开了个
   *   `hasSuccessChunk` 口子(有 SUCCESS 的 CHUNK 资产就放行),但门缝只开到「单镜也走 chunk
   *   编排管线」的那一支,没堵上真正的缺口。
   *
   * ★VIDEO-FINAL-ONLY-1(0904 用户实报「**直连模型不走编排**的任务,详情页没有加 logo/成片编辑
   *   入口」):这类任务(ai 侧 `orchestrator.py` 裸发单镜路 —— `panelScenes` 整个是空的,连
   *   1 项都没有,压根不会路由进 multi_shot/chunk 管线)资产形态只有一条 VIDEO_FINAL,`isMultiShot`
   *   和 `hasSuccessChunk` **都恒假**,闸原样挡死。跟 0901 那次是同一类病:UI 判据钉的是「像不像
   *   多镜任务」的形容词,不是「后端到底受不受理」。
   *
   * ★判据钉死 **后端 `composeFinal` 到底受理什么**(三分支,详见 `VideoService#composeFinal`):
   *   1. 有 SUCCESS 的 CHUNK → 收段 url 交 ai 拼段(0901 那支);
   *   2. 无 CHUNK 但多镜(panelCount≥2)→ 收齐全 N 镜 SUCCESS 的 SHOT;
   *   3. 都没有,但任务本身已经有一条 SUCCESS 的成片(FINAL-only,直连模型任务只会落这支)→
   *      直接把那条成片(优先 `VIDEO_FINAL_CLEAN` 纯净版,没有才退回 `VIDEO_FINAL`)当唯一源交
   *      ai 重新装配(overlay/字幕/BGM 全部走同一套流水线,ai 侧 concat 装配段本就显式支持
   *      单元素输入)。
   *   三支的合取(chunk 存在 / 多镜齐镜 / 有成片)展开后,**唯一在三支里都必要的公共条件就是
   *   `realFinal` 已是 SUCCESS**——分支 1/2 的产物就是把 CHUNK/SHOT 拼成那条 FINAL,分支 3 直接
   *   要求它已存在。`isMultiShot || hasSuccessChunk` 这半个条件已经被分支 3 补全后的受理面完全
   *   盖住,不再是独立约束,删掉它不影响任何既有行为,只补上直连任务这一种此前被误挡的情形。
   *   反向也仍然守住:没有任何成片的任务(还在跑/彻底失败)`realFinal` 为空或非 SUCCESS,闸依旧
   *   关着 —— 不会给一个必然落「非多镜任务无需合成」的按钮。
   * ★钉 `realFinal` 而不是 `finalAsset`:后者对单镜任务会兜底成 `assets[0]` ——
   *   成片还没拼出来时那是**段视频**,拿它开「调版式」等于让用户去重排一个还不存在的成片。
   *   多镜路 `finalAsset === realFinal`,这条对既有行为零影响。
   * ★`pickBaseImage` 对直连任务的取值:优先 `finalAsset.thumbUrl`——ai 侧裸发路
   *   (`orchestrator.py`)在上传 FINAL 前就 `extract_thumb` 抽帧上传,thumbKey 与视频同一次
   *   回调落库,和多镜/chunk 任务同一条底图管线,不需要额外兜底;`task.coverThumbUrl` 在详情接口
   *   恒为 null(封面批签只在列表接口做),真正兜底位是它后面的 `payload.firstFrameUrl`(I2V 建
   *   任务时的源图,签名有 TTL,过期后为空——与本闸无关的既有限制,不在本次修复范围)。
   */
  const composeEditable =
    realFinal != null && realFinal.status === "SUCCESS" && pickBaseImage(task, finalAsset) != null;

  if (error) {
    return (
      <>
        <div className="w-full">
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        </div>
      </>
    );
  }
  if (!task)
    return (
      <>
        <div className="w-full">加载中...</div>
      </>
    );

  const cleanAsset = task.assets?.find((a) => a.kind === "FINAL_CLEAN") ?? null;
  // CHUNK 资产(P2d-2 chunk 编排任务):有则详情页走段展示,旧 SHOT 任务走 per-shot 网格(按 kind 两态共存)。
  const chunkAssets = (task.assets ?? []).filter((a) => a.kind === "CHUNK");
  const hasChunks = chunkAssets.length > 0;
  /**
   * 段账本 —— 本页 chunk 控制条(续渲 / 合成成片 / 那句提示)的**单一真源**。
   *
   * ★2026-08-19 撤回旧口径:这里原来钉 `task.status === "PARTIAL"`,而上一行原有一条注释写着
   *   「PARTIAL 失败段不落 CHUNK 资产,chunkAssets 只含成功前缀」——**那条注释是错的**。
   *   `VideoAssetDto.status` 的注释才是今天的事实:「分块计划先于渲染下发,段数一定就落 N 条
   *   PENDING」。真单 5219008119809 实测段行是 `[SUCCESS, SUCCESS, SUCCESS, PENDING]`。
   *
   *   钉 `task.status` 的代价:单段重渲成功后 ai 无条件回 `SUCCESS`(它说的是「这次重渲成功」,
   *   而那字段是整单终态)→ 整单被翻成 SUCCESS 而第 4 段还是 PENDING →「继续生成」按钮
   *   **整个不渲染**,用户唯一能补齐的入口消失,任务却显示「已完成」。见 chunkTally.ts 头注。
   */
  const chunkTally = tallyChunks(chunkAssets);
  const allChunksReady = chunkTally.allReady;
  /**
   * 这一单有没有写口播 —— 决定要不要提醒「逐段试听没有人声」(G-02)。
   * ★voiceovers 定义在下方(第 331 行 payload 解析处),这里只在渲染期读,不存在 TDZ 问题;
   *   放这儿是为了紧挨 allChunksReady,两者服务同一条提示。
   */
  const hasVoiceovers = () => voiceovers.some((v) => (v ?? "").trim().length > 0);
  // 两个闸都钉段账本:段齐了才让合成,出过一些又缺一些才劝续渲。都不再问 task.status ——
  // 那个字段恰恰是会被算错的那一个(而它算错时,用户正好最需要这两个入口)。
  const canComposeChunk = chunkTally.allReady && !isActive;
  const canResume = chunkTally.partial && !isActive;
  // ★纯派生,与 chunkAssets 一起算在早返回之前 —— 本仓白屏 P0 的成因正是「首帧少调一帧多调」,
  //   把派生值跟着前置最省心(本条不是 hook,但同一处算,读的人不用猜顺序)。
  const resumeNotice = partialResumeNotice(chunkAssets);
  // seq(1-based)→ SHOT 资产
  const shotAssetBySeq = new Map<number, NonNullable<typeof task.assets>[number]>();
  (task.assets ?? []).filter((a) => a.kind === "SHOT").forEach((a) => shotAssetBySeq.set(a.seq, a));
  // 全镜都出片(SUCCESS)→ 可合成成片;成片过期(finalStale)→ 提示重新合成。
  const allShotsReady =
    isMultiShot &&
    Array.from({ length: shotCount }, (_, i) => shotAssetBySeq.get(i + 1)).every(
      (a) => a?.status === "SUCCESS"
    );
  const canCompose = allShotsReady && !isActive;

  const theme =
    (task.script && typeof task.script === "object" && "theme" in task.script
      ? String((task.script as { theme: unknown }).theme)
      : undefined) ?? (rp(payload, "theme") as string | undefined);
  const voiceTone = rp(payload, "voiceTone") as string | undefined;
  const voiceEmotion = rp(payload, "voiceEmotion") as string | undefined;
  const voiceSpeechRate = rp(payload, "voiceSpeechRate") as number | undefined;
  const voiceVolume = rp(payload, "voiceVolume") as number | undefined;
  const fontStyle = rp(payload, "fontStyle") as string | undefined;
  const brandFocus = rp(payload, "brandFocus") as string | undefined;
  const transition = rp(payload, "transition") as string | undefined;
  const bgmMood = rp(payload, "suggestedBgmMood") as string | undefined;
  // 用户选的风格:优先持久化的 styleCode(预设 key),否则 briefStyle(自定义文本)→ 短标签。
  const styleCode = rp(payload, "styleCode") as string | undefined;
  const briefStyle = rp(payload, "briefStyle") as string | undefined;
  const styleLabel = videoStyleLabel(styleCode, briefStyle);
  // 调性档(0924 车 C):建任务时随 requestPayload 落库;老任务没有 = 不显示(不编造档位)。
  const toneRegisterText = toneRegisterLabel(rp(payload, "toneRegister"));
  // 导演判的画面大字档:渲染载体 hookPlan.textOverlay(随 hookPlan 原样透传落库),只读展示。
  const textOverlayLine = textOverlayText(
    textOverlayOf(rp(payload, "hookPlan"), rp(payload, "textOverlay"))
  );
  // 目的(purpose):这条片「为了啥」——全片最重要的语义属性,决定 AI 按哪套叙事弧编排。
  // 此前详情页整个不显示它(创建时选了、落库了,看片时却查无此项)。落库口径是 label 原文
  // (「产品带货」),图生视频/让图动起来这类无目的任务为 null → 缺省不渲染。
  const purpose = (rp(payload, "purpose") as string | undefined)?.trim() || undefined;

  // ★播放器与"还没出片"占位:tab 版与无 tab 版**共用同一份**。
  //   重排时它一度被复制成两份 —— 那意味着以后往失败提示里加东西的人要加两遍,
  //   而漏掉的那一半不会报错,只是某些任务态下看不见。单源在此。
  const stagePlayer = (
    <>
      <CardContent className="p-0">
        {finalAsset && finalAsset.videoUrl ? (
          <div
            className={cn(
              // VID-D:中栏是"舞台" —— 黑底铺满整列、竖版片居中,读起来像播放器。
              // (旧值 mx-auto max-w-sm 是为 420px 左轨收黑边用的;在宽中栏里它让
              //  视频缩在一小块灰底中间,实拍看着像没做完。)
              "relative flex justify-center bg-black"
            )}
          >
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={stableUrl(finalAsset.id, finalAsset.generatedAt, finalAsset.videoUrl)}
              poster={finalAsset.thumbUrl}
              controls
              className="max-h-[58vh] max-w-full"
              style={{ aspectRatio: ratioToCss(task.ratio, "16 / 9") }}
            />
            {/* 1030:成片合成降级(字幕 / 大字没烧上)紧贴播放器亮出来,不藏进分段区。 */}
            {finalDegradeNotice(finalAsset.degradeReason) && (
              <div
                data-testid="final-degrade-notice"
                className="absolute inset-x-0 bottom-0 z-10 bg-amber-500/90 px-3 py-1.5 text-center text-2xs font-medium text-white"
              >
                {finalDegradeNotice(finalAsset.degradeReason)}
              </div>
            )}
            {/* 重新合成进行中:盖一层「重新合成中」,旧片不可点(避免点完没反应的困惑)。 */}
            {isActive && (
              <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-black/55 text-sm font-medium text-white">
                <Loader2 className="h-4 w-4 animate-spin" />
                重新合成中…
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-center bg-muted/40">
            <div
              className="flex max-h-[52vh] w-full max-w-md items-center justify-center px-6 py-10 text-center text-sm text-muted-foreground"
              style={{ aspectRatio: ratioToCss(task.ratio, "16 / 9") }}
            >
              {/* 这里原本按 status==="RENDERING" 分出两句更友好的文案(「AI 渲染中(约 1-5 分钟)」/
                    「正在合成成片…」)。但后端从不产 RENDERING —— media_task.status 只有 7 态 ——
                    所以那两句**一次都没渲染过**,用户看到的一直是下面这句兜底。2026-07-16 删死分支,
                    行为与线上完全一致。要区分「排队 vs 处理中」得用真实的 PENDING/RUNNING,那是另一件事。 */}
              {isActive
                ? "视频生成中,完成后会自动加载(详情页会自动刷新)…"
                : task.status === "PARTIAL"
                  ? maskVendor(task.errorMessage) ||
                    "部分镜未出片,其余镜已就绪;在下方重渲/编辑补齐失败镜后,点「合成成片」出片"
                  : task.status === "FAILED" || task.status === "BLOCKED"
                    ? maskVendor(task.errorMessage) || "生成失败,可点「整体重试」"
                    : "暂无成片,补齐分镜后点「合成成片」"}
            </div>
          </div>
        )}
        {/*
         * VID-REFUND-1:失败态**恒定**追加「你已经拿到什么」。
         *
         * ★贴在 `stagePlayer` 这个**单源**里(2026-08-19 b5 抽出):重排一度把「播放器 + 占位」
         *   复制成 tab 版 / 无 tab 版两份逐字相同的块,照旧改只会改到其中一份,另一份在某些
         *   任务态下静默漏掉。这里只有一处,加一遍即可。
         * ★为什么并列而不是替换上面那句:上面走 `task.errorMessage || 兜底文案` —— 后端越是
         *   给出详细错因,用户越看不到东西在哪儿,方向正好反了。错因照显示,这块不受影响。
         * ★为什么非有不可(0818 账目实据):失败单里编排是**真跑成功并扣了费**的(裸 uuid 键
         *   + gpt-5.5,20 条与任务 created_at 毫秒级对应),买到的分镜脚本 + 口播稿就存在任务行
         *   里、也就在「分镜渲染结果」标签页 —— 但页面一个字都没说,用户以为钱白花了。
         * ★不印金额:同步路径 settle 幂等键是裸 uuid,前端没有任何键能把 xb_billing_event 那笔
         *   账关联回这一单(已立独立缺陷 BILL-TRACE-1)。取不到真数就一个数字都不印。
         */}
        {/*
         * PARTIAL 续渲说明(老板 2026-08-19「详细一点」)。与上面那句**并列**不替换 ——
         * 上面走 `task.errorMessage || 兜底`,错因照显示;这块只回答「我拿到了什么 / 钱怎么算 /
         * 接下来点哪」。★段号是真值,算不出来时函数自己退化成不带数字的版本。
         */}
        {task.status === "PARTIAL" && (
          <div
            className="border-t bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            data-testid="video-partial-resume-note"
          >
            <div className="font-medium">{resumeNotice.headline}</div>
            {resumeNotice.body.map((line) => (
              <div key={line} className="mt-1">
                {line}
              </div>
            ))}
          </div>
        )}
        {(task.status === "FAILED" || task.status === "BLOCKED") && (
          <div
            className="border-t bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
            data-testid="video-failed-deliverable-note"
          >
            <div className="font-medium">成片没有出来,但这一单的分镜脚本和口播稿已经生成好了</div>
            <div className="mt-1">
              就在「分镜渲染结果」里,可以逐镜查看 / 编辑,也可以改完直接「合成成片」。本次已产生
              <b>编排环节</b>的积分消耗(生成脚本那一步);
              {billingLink ? "具体金额可在积分明细里看。" : "具体金额要管理员在积分流水里看。"}
            </div>
            {/*
             * ★按角色给入口(0819 真浏览器 e2e 抓到):`/my-billing` 在 ProtectedRoute 的角色矩阵里是
             *   **TENANT_ADMIN 专属**,MANAGER / STAFF / MEMBER 直访一律 403。此前这里对所有人都渲染
             *   链接 —— jsdom 那几条锁只断言 href,**href 是对的所以全绿**,而真实点击落到 /403。
             * ★给一个必然 403 的链接比不给更糟:它把「我看不到」变成「系统坏了」。所以非 ADMIN
             *   不给链接、只把话说清楚(不新增页面、不改权限,只是不说假话)。
             * ★0902 起 MANAGER/STAFF 有了自己的自查页 /my-credits(积分明细透明化批),此前这里仍
             *   只认 TENANT_ADMIN → MANAGER/STAFF 白白落回纯文字分支,是本批要修的过时点
             *   (2026-09-03):按 canSeeBilling/canSeeMyCredits 分流到各自能打开的页面。
             */}
            {billingLink && (
              <Link
                to={billingLink.to}
                className="mt-1.5 inline-flex items-center underline underline-offset-2"
              >
                {billingLink.label}
              </Link>
            )}
          </div>
        )}
      </CardContent>
      {finalAsset && finalAsset.status === "SUCCESS" && isActive && (
        <div className="flex items-center justify-center gap-2 border-t p-3.5 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          重新合成中,完成后自动更新…
        </div>
      )}
    </>
  );

  return (
    <>
      {/* VID-OVL-3(2026-08-18 老板拍板方案①):给悬浮球留一条右侧球道。
          实测几何:内容右缘恒 = 视口−24,球左缘 = 视口−72 → 固定 48px 交叠,**竖向怎么挪都躲不掉**
          (VID-OVL-2 的 9×2×3 矩阵结论)。留 56px 后内容右缘退到视口−80,与球之间还有 8px 气隙。
          只在 xl(≥1280)开:窄屏本就左右堆叠、横向余量紧,平白吃掉一截不划算(实测 1024 下
          分镜网格仅 2 列,再砍就掉列)。掉列代价实测:1280→3 列 / 1440→4 列 / 1920→6 列,加球道后不变。 */}
      <div className="w-full space-y-5 xl:pr-14">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">{task.name}</h1>
            {/* 内容层(主):目的 + 主题 —— 「这条片为了啥/讲什么」是看片时最先要知道的两件事。
                目的用实底色药丸置顶(全片最重要的语义属性),主题跟在后面。此前这两项都埋在
                「本次设置」里、和一堆技术参数平权,目的甚至整个不显示 —— 老板点名的「目的这种
                重要属性显示得很小」正指此处。 */}
            {(purpose || theme) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                {purpose && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary">
                    <Target className="h-3.5 w-3.5" />
                    {purpose}
                  </span>
                )}
                {theme && (
                  <span className="min-w-0 truncate text-sm text-foreground/85" title={theme}>
                    {theme}
                  </span>
                )}
              </div>
            )}
            {/* 技术规格层(次):状态 + 镜数/比例/清晰度/时长 —— 比内容层小一档,拉开主次。 */}
            <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap items-center gap-1">
              <Badge variant="outline" className={taskStatusBorderedBadgeClass(task.status)}>
                {taskStatusLabel(task.status)}
              </Badge>
              {isMultiShot ? (
                <>
                  {" · "}
                  {shotCount} 镜 · {ratioLabel(task.ratio)} · {task.resolution}
                </>
              ) : task.type === "DRAMA" ? (
                <>
                  {" · 漫剧 · "}
                  {task.script &&
                  typeof task.script === "object" &&
                  "shots" in task.script &&
                  Array.isArray((task.script as { shots: unknown[] }).shots)
                    ? (task.script as { shots: unknown[] }).shots.length
                    : 0}
                  {" 镜 · "}
                  {task.durationSec ?? 0}s
                </>
              ) : (
                <>
                  {" · "}
                  {videoProviderLabel(task.provider, task.model, task.modelLabel)} ·{" "}
                  {task.resolution} · {ratioLabel(task.ratio)} · {task.durationSec}s
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* ★仅排队中可取消(2026-08-03):RUNNING 的渲染 ai 侧 cancel 是 no-op,取消=谎报成功 */}
            {task.status === "PENDING" && (
              <Button variant="outline" onClick={onCancel} className="cursor-pointer">
                取消
              </Button>
            )}
            {["FAILED", "BLOCKED", "CANCELLED"].includes(task.status) && (
              <Button onClick={onRetry} className="cursor-pointer">
                整体重试
              </Button>
            )}
            {/*
             * 失败态的「回去改稿」出口(0811 玉玉「视频提交失败,返回啥也没有了,所有内容要重新填写」;
             * 0813 琳琳 / 娜娜同诉求)。
             *
             * ★此前失败态只有「整体重试」和「返回列表」——**原样再跑一遍**或**从零重填**,
             *   中间那一档(改一改再出)没有入口。而失败里有一大类恰恰是内容问题
             *   (文案太长、审核不过),原样重试必然再失败一次,钱和时间都白花。
             * ★不动「整体重试」的位置和样式:玉玉那一单实际是瞬时故障(第四次就成了),
             *   重试对那一类是对的。这里只是**补上缺的那个出口**,不去替用户判断该走哪条。
             * ★两条路按有没有草稿分流:失败任务的来源草稿**还在**(后端只在成片成功时才软删它),
             *   有就直接回草稿(整份表单都在);没有就至少把文案带回去,别让人从零重打。
             */}
            {["FAILED", "BLOCKED"].includes(task.status) && reviseTarget && (
              <Button
                variant="outline"
                data-testid="video-revise-after-fail"
                onClick={reviseTarget.go}
                className="cursor-pointer"
              >
                {reviseTarget.label}
              </Button>
            )}
            {task.status === "SUCCESS" && (
              <Button onClick={onRecreate} className="cursor-pointer">
                再创作一个
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/ai-video")}
              className="cursor-pointer"
            >
              返回列表
            </Button>
          </div>
        </div>

        {actionError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {actionError}
          </div>
        )}

        {/* ── VID-D 三列工作台(2026-08-18 老板选定 D 版:「第一栏就显示视频有点浪费」)──
            左 200px 工具栏(音轨开关 / 字幕版式 / 应用)· 中 大画面(播放 ⇄ 调版式 同一块画布)·
            右 226px 动作与这条片的参数。<lg 退回纵向堆叠,DOM 顺序 = 画面 → 工具 → 动作,
            读屏与 tab 序仍是「先看到片,再动它」。
            ★分镜/分段那一大块留在三列**下方**通栏 —— 每镜网格塞进 226px 会碎成条,
              D 稿右栏那份只是速览(点它滚下去)。 */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          {/* ── 中:大画面(DOM 顺序上排第一 —— 先看到片) ── */}
          <div data-testid="video-detail-stage-col" className="order-1 min-w-0 flex-1 lg:order-2">
            <Card className="overflow-hidden">
              {composeEditable ? (
                <Tabs defaultValue="play" className="w-full">
                  <div className="border-b px-3 pt-2.5">
                    <TabsList>
                      <TabsTrigger value="play">播放</TabsTrigger>
                      <TabsTrigger value="layout">调版式</TabsTrigger>
                    </TabsList>
                  </div>
                  <TabsContent value="play" className="mt-0">
                    {stagePlayer}
                  </TabsContent>
                  <TabsContent value="layout" className="mt-0 p-4">
                    {/* 同一块画布同时管 logo 与字幕 —— 拖完切回「播放」看的还是这一版。
                        ★宽度按**视口高**收:底图是 w-full、高度随原图比例走,竖版片在 max-w-md
                        下会长到 ~800px、底边跑出屏幕 —— 而字幕默认就贴在底边,等于「要拖的那个
                        东西看不见」(真浏览器实拍确认)。竖版按 33vh 折算宽度,横版不受这条限制。 */}
                    <div
                      className="mx-auto"
                      style={{ maxWidth: isLandscape(task.ratio) ? "42rem" : "min(28rem, 33vh)" }}
                    >
                      {composeEditor.canvas}
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <>{stagePlayer}</>
              )}
            </Card>
          </div>

          {/* ── 左:工具栏(<lg 排在画面之后) ── */}
          {composeEditable && (
            <div
              data-testid="video-detail-tool-col"
              className="order-2 w-full min-w-0 lg:order-1 lg:sticky lg:top-4 lg:w-[212px] lg:shrink-0"
            >
              <Card>
                <CardContent className="space-y-3 p-3">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <div className="min-w-0">
                      <h3 className="text-xs font-semibold leading-tight">成片编辑</h3>
                      <p className="text-2xs leading-tight text-muted-foreground">
                        秒级重出片,不花积分
                      </p>
                    </div>
                  </div>
                  {composeEditor.controls}
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── 右:动作 + 这条片(<lg 排在最后) ── */}
          <div
            data-testid="video-detail-meta-col"
            className="order-3 w-full min-w-0 space-y-4 lg:sticky lg:top-4 lg:w-[238px] lg:shrink-0"
          >
            {/* D 稿右栏第二块「这条片」:参数一眼看全,不用翻到下面的「本次设置」。
                ★只列**已知非空**的项 —— 补一堆「—」比不列更像出了问题。 */}
            <Card>
              <CardContent className="space-y-1.5 p-3">
                <p className="text-xs font-semibold">这条片</p>
                {/* 目的置顶且加重:它是这条片最重要的属性,不与下面的技术规格平权。 */}
                {purpose && (
                  <div className="flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-primary">
                    <Target className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs font-semibold">{purpose}</span>
                  </div>
                )}
                <dl className="space-y-1 text-2xs">
                  {(
                    [
                      ["比例", task.ratio ? ratioLabel(task.ratio) : null],
                      ["清晰度", task.resolution],
                      ["时长", finalAsset?.durationSec ? `${finalAsset.durationSec}s` : null],
                      ["镜数", isMultiShot ? `${shotCount} 镜` : null],
                      [
                        "大小",
                        finalAsset?.sizeBytes
                          ? `${(finalAsset.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                          : null,
                      ],
                      [
                        "消耗",
                        task.creditsUsed ? `${task.creditsUsed.toLocaleString()} 积分` : null,
                      ],
                      ["风格", styleLabel],
                    ] as [string, string | null | undefined][]
                  )
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between gap-2">
                        <dt className="shrink-0 text-muted-foreground">{k}</dt>
                        <dd className="min-w-0 truncate text-right font-medium" title={v!}>
                          {v}
                        </dd>
                      </div>
                    ))}
                </dl>
              </CardContent>
            </Card>

            {/* D 稿右栏第三块「分镜速览」:只读一眼;要动它点下去,大网格在通栏那一块。 */}
            {isMultiShot && panelScenes.length > 0 && (
              <Card>
                <CardContent className="space-y-1.5 p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-xs font-semibold">分镜</p>
                    <button
                      type="button"
                      data-testid="jump-to-shots"
                      onClick={() =>
                        document
                          .querySelector('[data-testid="video-shots-section"]')
                          ?.scrollIntoView({ behavior: "smooth", block: "start" })
                      }
                      className="text-2xs text-primary underline-offset-2 hover:underline"
                    >
                      {hasChunks ? "重渲某段" : "改某镜"}
                    </button>
                  </div>
                  <ol className="space-y-1 text-2xs text-muted-foreground">
                    {panelScenes.slice(0, 6).map((sc, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="shrink-0 font-mono text-foreground">镜{i + 1}</span>
                        <span className="min-w-0 truncate" title={sc}>
                          {sc}
                        </span>
                      </li>
                    ))}
                    {panelScenes.length > 6 && (
                      <li className="text-2xs">…还有 {panelScenes.length - 6} 镜</li>
                    )}
                  </ol>
                </CardContent>
              </Card>
            )}

            {finalAsset && finalAsset.status === "SUCCESS" && !isActive && (
              /* VID-LAYOUT-1:预览卡进了 420px 左轨,页脚不再横排(sm:flex-row 在轨内必挤爆
                 —— meta 块被压到逐字竖排、按钮溢出),恒纵排 + 按钮行自己换行。 */
              <div className="flex flex-col gap-3 border-t p-3.5">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium">成片已就绪</span>
                    {/* VID-D:比例/时长/大小已经在上面的「这条片」里了,这里不再重复一遍 ——
                        同一屏把同一组数字讲两遍,读者会以为它们是两件不同的事。
                        留下的是**只有这里才有**的:片子里到底烧进了什么(配音/字幕/BGM)。 */}
                    {(() => {
                      const tags = dropDegradedTags(
                        summarizeComposed(payload),
                        finalAsset.degradeReason
                      );
                      return tags.length > 0 ? (
                        <span className="flex flex-wrap items-center gap-1">
                          {tags.map((t) => (
                            <Badge key={t.key} variant="secondary" className="font-normal">
                              {t.label}
                            </Badge>
                          ))}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">纯画面</span>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => onDownload(finalAsset.id, finalAsset.seq)}
                    disabled={downloading}
                    className="cursor-pointer"
                  >
                    <Download className="mr-1.5 h-4 w-4" />
                    {downloading ? "准备中…" : "下载成片"}
                  </Button>
                  {/* E4/G1c(design §8「军师产出 → 用户"这个好,入库"」):把成片提名进知识库。
                      ★只挂**成片**,不挂单镜/chunk:入库的是"能发给客户的那件东西",
                      半成品进 KB 只会让审批台上堆满没人敢批的中间件。 */}
                  <NominateToKbButton taskId={task.id} asset={finalAsset} disabled={downloading} />
                  {/* BP-02:「视频脚本 → 数字人口播」生产端。消费端(presets 验型 + 创建页预填 +
                      sceneKey 打标)早就全套建好,但全仓零 navigate —— 这条链路从来没被触发过。
                      ★纯画面片(无逐镜口播)不给这个按钮:送过去只会得到空脚本,验型器整包丢弃,
                      用户点了等于没点。 */}
                  {dhScript && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="video-send-to-dh"
                      onClick={() =>
                        // 直指真人口播线:源视频的逐镜口播 → 真人出镜换台词,片型是确定的。
                        navigate(DH_LINE_ROUTE.talking, {
                          state: { scenePreset: { kind: "dh_from_video", script: dhScript } },
                        })
                      }
                      className="cursor-pointer"
                      title="把这条片的逐镜口播带到数字人创建页,做一条真人出镜口播"
                    >
                      送去数字人
                    </Button>
                  )}
                  {cleanAsset && cleanAsset.videoUrl && cleanAsset.status === "SUCCESS" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDownload(cleanAsset.id, cleanAsset.seq, "源视频")}
                      disabled={downloading}
                      className="cursor-pointer text-muted-foreground"
                      title="仅画面拼接,无字幕 / 配音 / BGM / 叠加,原始画质"
                    >
                      下载源视频
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          {/* ── 分镜 + 本次设置:Tabs 收纳,缩短页面长度 ── */}
          {/* data-testid 兼作右栏「分镜速览」的滚动锚点 */}
          <Tabs
            data-testid="video-shots-section"
            defaultValue={isMultiShot ? "shots" : "settings"}
            className="w-full"
          >
            <TabsList>
              {isMultiShot && (
                <TabsTrigger value="shots">
                  {hasChunks ? "分段渲染结果" : "分镜渲染结果"}
                </TabsTrigger>
              )}
              <TabsTrigger value="settings">本次设置</TabsTrigger>
            </TabsList>

            {isMultiShot && (
              <TabsContent value="shots" className="mt-3">
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium">{hasChunks ? "分段渲染结果" : "分镜渲染结果"}</h3>
                      <span className="hidden text-xs text-muted-foreground sm:inline">
                        {hasChunks ? "每段含若干镜,模型内部切镜" : "改完镜头后点「合成成片」出片"}
                      </span>
                    </div>
                    {hasChunks ? (
                      <>
                        {/* W0-② 任务级降级聚合:参考被拒不再只藏在段 chip 里(事故 4853196653568 全程无感)。 */}
                        {degradeSummary(chunkAssets) && (
                          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                            {degradeSummary(chunkAssets)}
                          </div>
                        )}
                        {/* chunk 控制条:出过一些又缺一些 → 可续渲;全段就绪 → 可(重新)合成成片。
                            ★提示与按钮**同一个判据**(chunkTally.partial)。它们此前各挂各的条件,
                              于是出现过「提示叫你点『继续生成』,而那颗按钮根本不在」的死路。 */}
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 p-2.5">
                          <div className="min-w-0 text-xs">
                            {chunkTally.partial ? (
                              <span
                                data-testid="chunk-resume-hint"
                                className="text-amber-600 dark:text-amber-500"
                              >
                                已生成 {chunkTally.delivered}/{chunkTally.planned} 段 ——
                                点「继续生成」 补齐剩余 {chunkTally.missing} 段并合成成片
                              </span>
                            ) : allChunksReady ? (
                              <span className="text-muted-foreground">
                                全部段已生成,可重渲某段后点「合成成片」用最新段重拼
                                {/* ★口播说明(测试反馈 G-02,雷 8·10「视频中没有口播」):
                                  下面每段的 <video> 是引擎直出的**原始画面**,不含口播人声 ——
                                  TTS 是在「合成成片」时才拼进去的(ai `_assemble_and_upload_final`:
                                  voiceovers → synthesize → concat)。段渲染那一步只有 lip_sync 段
                                  会用到 TTS,而且那是给模型对口型的**参考音频**,不是配到成品里的人声。
                                  不写这一句,用户逐段试听会以为写好的口播丢了 —— 雷就是这么报的。 */}
                                {hasVoiceovers() && (
                                  <span className="ml-1 text-foreground">
                                    · 下面逐段试听是<b>无人声的原始画面</b>,口播要合成成片后才有
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">段生成中…</span>
                            )}
                          </div>
                          <div className="flex shrink-0 gap-2">
                            {chunkTally.partial && (
                              <Button
                                size="sm"
                                className="h-8 cursor-pointer"
                                disabled={!canResume || composing}
                                onClick={onResume}
                              >
                                {composing ? "提交中…" : "继续生成"}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant={chunkTally.partial ? "outline" : "default"}
                              className="h-8 cursor-pointer"
                              disabled={!canComposeChunk || composing}
                              onClick={onComposeFinal}
                            >
                              {composing ? "提交中…" : "合成成片"}
                            </Button>
                          </div>
                        </div>
                        {/**
                         * 改文案 = **弹框**(2026-08-19 老板走查:「建议弹框形式会不会好一些」)。
                         *
                         * ★原来是内联展开。真机实测:一段 3 镜展开 9 个输入框、把页面撑高约
                         *   800px,**把正在改的那一段的画面整个挤出视野** —— 而对照画面改文案
                         *   正是这件事的全部意义。VID-D 当时把它放在网格上方就是为了「眼睛不用
                         *   动」,内联展开却把这个初衷抵消掉了。
                         * ★弹框还顺带解决:一次只专注一段(内联时下面还铺着别的段卡)。
                         */}
                        <Dialog
                          open={!!chunkDraft}
                          onOpenChange={(next) => {
                            if (!next) setChunkDraft(null);
                          }}
                        >
                          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="text-sm">
                                改第 {chunkDraft?.seq} 段的文案
                              </DialogTitle>
                            </DialogHeader>
                            {chunkDraft && (
                              <VideoChunkTextEditor
                                draft={chunkDraft}
                                onChange={patchChunkShot}
                                onCancel={() => setChunkDraft(null)}
                                onSave={onSaveChunkEdit}
                                saving={savingChunk}
                                disabled={isActive || composing}
                                shotDurations={panelDurations}
                              />
                            )}
                          </DialogContent>
                        </Dialog>
                        <VideoChunkGrid
                          chunks={chunkAssets}
                          ratio={task.ratio}
                          onRegen={onRegenChunk}
                          onEditText={openChunkEdit}
                          regenSeq={regenSeq}
                          busy={isActive || composing}
                          shotScenes={panelScenes}
                          shotVoiceovers={voiceovers}
                          shotMotions={panelMotions}
                          /* 终态 = 任务不会再自己往下跑了。此时 PENDING 段永远不会动,
                             文案要从「等前面的段渲完」换成指向「继续生成」——见组件里 taskSettled 注释。 */
                          taskSettled={!isActive}
                          /* ★与上面那颗「继续生成」按钮**同一个判据**(0829 走查:delivered=0 的
                             失败单里按钮不渲染,而段卡照旧写「点继续生成」,指向一颗不存在的按钮)。 */
                          canResume={chunkTally.partial}
                        />
                      </>
                    ) : (
                      <>
                        {/* 合成控制条:全镜出片可合成;成片过期(改过镜)高亮提示。 */}
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/30 p-2.5">
                          <div className="min-w-0 text-xs">
                            {!allShotsReady ? (
                              <span className="text-muted-foreground">
                                还有镜未出片,重渲补齐所有镜后才能合成成片
                              </span>
                            ) : task.finalStale ? (
                              <span className="text-amber-600 dark:text-amber-500">
                                你修改了镜头,成片已过期 —— 点「合成成片」用最新分镜重新出片
                              </span>
                            ) : (
                              <span className="text-muted-foreground">成片已是最新分镜</span>
                            )}
                          </div>
                          <Button
                            size="sm"
                            className="h-8 shrink-0 cursor-pointer"
                            disabled={!canCompose || composing}
                            onClick={onComposeFinal}
                          >
                            {composing ? "提交中…" : task.finalStale ? "重新合成成片" : "合成成片"}
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {Array.from({ length: shotCount }, (_, i) => {
                            const seq = i + 1;
                            const asset = shotAssetBySeq.get(seq);
                            // 任务正在被「重新出片」(渲染中 / 合成中 / 单镜重渲提交中)期间,不显示任何「失败」
                            // —— 这正是"点重新合成后报错信息还在"的根因:旧失败态在重渲窗口里残留。统一抑制。
                            const reprocessing =
                              isActive || composing || savingEdit || regenSeq !== null;
                            // 多镜部分失败:失败镜没上传 SHOT 资产,所以终态任务里"缺资产"的镜=失败镜
                            // (标红 + 可重渲补齐);重处理中一律按 pending 显示「渲染中」,不残留旧失败。
                            const shotFailed =
                              !reprocessing &&
                              (asset?.status === "FAILED" || asset?.status === "BLOCKED" || !asset);
                            const shotPending =
                              !shotFailed && (reprocessing || !asset || asset.status === "PENDING");
                            return (
                              <div key={seq} className="rounded-lg border bg-card p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="font-mono">
                                    镜 {seq}/{shotCount}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {panelDurations[i] ? `${panelDurations[i]}s` : ""}
                                  </span>
                                  <div className="flex-1" />
                                  {shotFailed && (
                                    <Badge variant="destructive" className="text-2xs">
                                      失败
                                    </Badge>
                                  )}
                                </div>
                                <div
                                  className="relative overflow-hidden rounded bg-black max-h-72 flex items-center justify-center"
                                  style={{ aspectRatio: ratioToCss(task.ratio, "9 / 16") }}
                                >
                                  {asset?.videoUrl && !shotPending ? (
                                    // eslint-disable-next-line jsx-a11y/media-has-caption
                                    <video
                                      src={stableUrl(asset.id, asset.generatedAt, asset.videoUrl)}
                                      poster={asset.thumbUrl}
                                      controls
                                      className="h-full w-full object-contain"
                                    />
                                  ) : firstFrameUrls[i] ? (
                                    // 没视频但有首帧图 → 拿首帧当预览(暗一档表示非成片)+ 底部状态条。
                                    <>
                                      <img
                                        src={firstFrameUrls[i]}
                                        alt={`镜 ${seq} 首帧`}
                                        className="h-full w-full object-contain opacity-75"
                                        onError={(e) => {
                                          (e.currentTarget as HTMLImageElement).style.display =
                                            "none";
                                        }}
                                      />
                                      <span className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-center text-2xs text-white">
                                        {shotPending && (isActive || regenSeq === seq)
                                          ? "本镜渲染中…(首帧预览)"
                                          : shotFailed
                                            ? "本镜失败 · 首帧预览,编辑后重渲"
                                            : "首帧预览"}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="px-2 text-center text-xs text-muted-foreground">
                                      {shotPending && (isActive || regenSeq === seq)
                                        ? "本镜渲染中…"
                                        : shotFailed
                                          ? asset?.errorCode || "本镜失败"
                                          : "暂无本镜视频"}
                                    </span>
                                  )}
                                </div>
                                {panelScenes[i] && (
                                  <p
                                    className="text-xs text-muted-foreground line-clamp-3"
                                    title={panelScenes[i]}
                                  >
                                    {panelScenes[i]}
                                  </p>
                                )}
                                {voiceovers[i] && (
                                  <p className="text-xs text-foreground">
                                    <span className="text-muted-foreground">口播:</span>
                                    {voiceovers[i]}
                                  </p>
                                )}
                                {editSeq === seq ? (
                                  <div className="space-y-2 rounded-md border bg-muted/20 p-2">
                                    <div>
                                      <p className="mb-1 text-2xs text-muted-foreground">
                                        画面描述(这一镜拍的是什么)
                                      </p>
                                      <Textarea
                                        rows={3}
                                        value={editForm.panelPrompt}
                                        onChange={(e) =>
                                          setEditForm((f) => ({
                                            ...f,
                                            panelPrompt: e.target.value,
                                          }))
                                        }
                                        className="text-xs"
                                        placeholder="改这一镜的画面(被审核拒时,去掉/换掉敏感词再重渲)"
                                      />
                                    </div>
                                    <div>
                                      <p className="mb-1 text-2xs text-muted-foreground">
                                        运动 / 动作(镜头和主体怎么动)
                                      </p>
                                      <Textarea
                                        rows={2}
                                        value={editForm.panelMotion}
                                        onChange={(e) =>
                                          setEditForm((f) => ({
                                            ...f,
                                            panelMotion: e.target.value,
                                          }))
                                        }
                                        className="text-xs"
                                        placeholder="例:镜头缓慢推近 / 手部特写横移"
                                      />
                                    </div>
                                    <div>
                                      <p className="mb-1 text-2xs text-muted-foreground">
                                        口播文案(改后下次「合成成片」生效,不重渲视频)
                                      </p>
                                      <AutoGrowTextarea
                                        value={editForm.voiceover}
                                        onChange={(e) =>
                                          setEditForm((f) => ({ ...f, voiceover: e.target.value }))
                                        }
                                        className="text-xs"
                                      />
                                    </div>
                                    <div>
                                      <p className="mb-1 text-2xs text-muted-foreground">
                                        负面词(需避免出现的内容)
                                      </p>
                                      <Textarea
                                        rows={2}
                                        value={editForm.negativePrompt}
                                        onChange={(e) =>
                                          setEditForm((f) => ({
                                            ...f,
                                            negativePrompt: e.target.value,
                                          }))
                                        }
                                        className="text-xs"
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        className="h-7 flex-1 cursor-pointer text-xs"
                                        disabled={savingEdit || isActive}
                                        onClick={() => onSaveEdit(true)}
                                      >
                                        {savingEdit ? "提交中…" : "保存并重渲"}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 cursor-pointer text-xs"
                                        disabled={savingEdit}
                                        onClick={() => onSaveEdit(false)}
                                      >
                                        仅保存
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 cursor-pointer text-xs"
                                        disabled={savingEdit}
                                        onClick={() => setEditSeq(null)}
                                      >
                                        取消
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex gap-2 pt-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 flex-1 cursor-pointer text-xs"
                                      disabled={isActive || regenSeq !== null}
                                      onClick={() => onRegenShot(seq)}
                                    >
                                      {regenSeq === seq ? "重渲中…" : "重渲本镜"}
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 cursor-pointer text-xs"
                                      disabled={isActive || regenSeq !== null}
                                      onClick={() => openEdit(seq)}
                                    >
                                      编辑
                                    </Button>
                                    {asset?.videoUrl && asset.status === "SUCCESS" && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 cursor-pointer text-xs"
                                        disabled={downloading}
                                        onClick={() => onDownload(asset.id, seq)}
                                      >
                                        下载
                                      </Button>
                                    )}
                                    {/* 删除本镜:某镜一直出不来就删掉;多镜至少保留 2 镜(>2 时才显示)。 */}
                                    {shotCount > 2 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 cursor-pointer text-xs text-muted-foreground hover:text-destructive"
                                        disabled={isActive || regenSeq !== null}
                                        onClick={() => onDeleteShot(seq)}
                                      >
                                        删除
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {isActive && (
                          <p className="text-xs text-muted-foreground">
                            渲染中,各镜陆续出片;单镜重渲只重渲该镜(不自动合成),改完点「合成成片」出片。
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            <TabsContent value="settings" className="mt-3">
              <Card>
                <CardContent className="p-4 space-y-3 text-sm">
                  <h3 className="font-medium">本次设置</h3>

                  {task.type === "DRAMA" ? (
                    <DramaSettings task={task} />
                  ) : (
                    <>
                      {/* 目的 + 主题 = 内容层,置于技术参数网格之上并加重,拉开主次。 */}
                      {purpose && (
                        <div>
                          <span className="text-foreground font-medium">目的:</span>{" "}
                          <span className="font-semibold text-primary">{purpose}</span>
                        </div>
                      )}
                      {theme && (
                        <div>
                          <span className="text-foreground font-medium">主题:</span>{" "}
                          <span className="text-muted-foreground">{theme}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-muted-foreground md:grid-cols-3">
                        <div>
                          <span className="text-foreground">模型:</span>{" "}
                          {videoProviderLabel(task.provider, task.model, task.modelLabel)}
                        </div>
                        <div>
                          <span className="text-foreground">比例/清晰度:</span>{" "}
                          {ratioLabel(task.ratio)} · {task.resolution}
                        </div>
                        <div>
                          <span className="text-foreground">模式:</span>{" "}
                          {/* 1006 复测:此前直出码「I2V」—— 与生成模式下拉同一张中文表 */}
                          {isMultiShot
                            ? `多镜(${shotCount})`
                            : generationModeLabel(task.generationMode)}
                        </div>
                        {styleLabel && (
                          <div>
                            <span className="text-foreground">风格:</span> {styleLabel}
                          </div>
                        )}
                        {toneRegisterText && (
                          <div data-testid="settings-tone-register">
                            <span className="text-foreground">调性:</span> {toneRegisterText}
                          </div>
                        )}
                        {textOverlayLine && (
                          <div data-testid="settings-text-overlay">
                            <span className="text-foreground">画面大字:</span> {textOverlayLine}
                          </div>
                        )}
                        {voiceTone && (
                          <div>
                            <span className="text-foreground">配音:</span>{" "}
                            {VOICE_TONE_LABEL[voiceTone] || "默认音色"}
                            {voiceEmotion ? ` · ${EMOTION_LABEL[voiceEmotion] || "默认情绪"}` : ""}
                          </div>
                        )}
                        {(voiceSpeechRate != null || voiceVolume != null) && (
                          <div>
                            <span className="text-foreground">语速/音量:</span>{" "}
                            {voiceSpeechRate ?? "—"} · {voiceVolume ?? "—"}
                          </div>
                        )}
                        {fontStyle && (
                          <div>
                            <span className="text-foreground">字幕字体:</span>{" "}
                            {FONT_LABEL[fontStyle] || "默认字体"}
                          </div>
                        )}
                        {bgmMood && (
                          <div>
                            <span className="text-foreground">BGM 情绪:</span> {bgmMood}
                          </div>
                        )}
                        {transition && isMultiShot && (
                          <div>
                            <span className="text-foreground">转场:</span>{" "}
                            {TRANSITION_LABEL[transition] || "默认转场"}
                          </div>
                        )}
                        <div>
                          <span className="text-foreground">来源:</span>{" "}
                          {task.source === "FROM_POSTER"
                            ? "海报一键成片"
                            : task.source === "TEMPLATE"
                              ? `模板${task.templateName ? ` (${task.templateName})` : ""}`
                              : "自由创作"}
                        </div>
                        {/* M2 成片档案(P0-2 §5-6):蓝图溯源随 sourceRef 落任务。DTO 顶层 sourceRef
                          在 mapper 里恒 null(历史口径),真源是 requestPayload.sourceRef —— 双读兜底
                          (codex 复审修:只读顶层则「本片套用」永不显示)。 */}
                        {(() => {
                          const ref = (task.sourceRef ??
                            (task.requestPayload as Record<string, unknown> | undefined)?.[
                              "sourceRef"
                            ] ??
                            {}) as Record<string, unknown>;
                          if (typeof ref.explosiveFormulaName !== "string") return null;
                          return (
                            <div>
                              <span className="text-foreground">本片套用:</span>{" "}
                              {ref.explosiveFormulaName}
                              {ref.explosiveFidelityMode === "copy" ? " · 仿拍" : " · 学套路"}
                            </div>
                          );
                        })()}
                        {/* 波2 拍法回显:随 sourceRef 落任务(同蓝图溯源的自由 Map 通道)。
                          ★三态如实:引擎用了哪张 / 是不是用户选的 / 用户选了但没套用。
                          最后一态最要紧 —— 贴现成分镜脚本时引擎忠实拆解、不套拍法,
                          若把「用户选的」当结果显示,概览就比实际乐观(codex 抓过两次的形态)。 */}
                        {(() => {
                          const ref = (task.sourceRef ??
                            (task.requestPayload as Record<string, unknown> | undefined)?.[
                              "sourceRef"
                            ] ??
                            {}) as Record<string, unknown>;
                          const applied =
                            typeof ref.formulaName === "string" ? ref.formulaName : null;
                          const picked =
                            typeof ref.pickedFormulaName === "string"
                              ? ref.pickedFormulaName
                              : null;
                          const byUser =
                            !!picked &&
                            !!ref.pickedFormulaKey &&
                            ref.pickedFormulaKey === ref.formulaKey;
                          if (!applied && !picked) return null;
                          return (
                            <div>
                              <span className="text-foreground">拍法:</span>{" "}
                              {applied ? (
                                <>
                                  {applied}
                                  <span className="text-muted-foreground">
                                    {byUser ? " · 你选的" : " · 引擎自动"}
                                  </span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">
                                  未套用(你选的「{picked}」这次没生效)
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {task.creditsUsed != null && (
                          <div>
                            {/* P0.6 车 B(2026-09-15):后端改口径——有真实扣费台账(settle)时这里是
                            这一单的累计真实扣费(含重出/重渲,不再是"最近一次"被覆盖掉之前的历史);
                            台账还没 settle(生成中/老数据兜底)时退回上一版估算值。不再需要"本次"
                            这个免责修饰,故改名"本单已消耗积分"。 */}
                            <span className="text-foreground">本单已消耗积分:</span>{" "}
                            {task.creditsUsed}
                          </div>
                        )}
                      </div>

                      {brandFocus && (
                        <div>
                          <span className="text-foreground font-medium">品牌聚焦:</span>{" "}
                          <span className="text-muted-foreground">{brandFocus}</span>
                        </div>
                      )}
                      {/* 契约 v2:artDirection(美术方向)已废 —— 风格由用户选的预设一次定调,
                        导演不再产美术圣经,详情页也不再有这块可展示。 */}

                      {/* 每镜参数折叠 */}
                      {isMultiShot && (
                        <details className="rounded border bg-muted/20">
                          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                            每镜参数({shotCount} 镜)
                          </summary>
                          <div className="space-y-3 p-3 pt-0">
                            {Array.from({ length: shotCount }, (_, i) => (
                              <div key={i} className="rounded border bg-background/40 p-2 text-xs">
                                <div className="font-medium text-foreground mb-1">
                                  镜 {i + 1}
                                  {panelDurations[i] ? ` · ${panelDurations[i]}s` : ""}
                                </div>
                                {panelScenes[i] && (
                                  <div className="text-muted-foreground">
                                    <span className="text-foreground">环境:</span> {panelScenes[i]}
                                  </div>
                                )}
                                {panelMotions[i] && (
                                  <div className="text-muted-foreground">
                                    <span className="text-foreground">动作:</span> {panelMotions[i]}
                                  </div>
                                )}
                                {voiceovers[i] && (
                                  <div className="text-muted-foreground">
                                    <span className="text-foreground">口播:</span> {voiceovers[i]}
                                  </div>
                                )}
                                {negativePrompts[i] && (
                                  <div className="text-muted-foreground/70">
                                    <span className="text-foreground">负面:</span>{" "}
                                    {negativePrompts[i]}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      {/* 1006:对客不露系统段(【】/★/英文风格串),标题去「prompt」技术词 */}
                      {!isMultiShot && stripSystemPromptLines(task.promptFinal) && (
                        <div className="mt-1">
                          <div className="text-foreground font-medium mb-1">画面描述</div>
                          <pre className="whitespace-pre-wrap text-xs bg-muted p-2 rounded text-muted-foreground">
                            {stripSystemPromptLines(task.promptFinal)}
                          </pre>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}

/** DRAMA 漫剧的设置区(沿用旧展示)。 */
function DramaSettings({
  task,
}: {
  task: NonNullable<ReturnType<typeof useVideoTaskPolling>["task"]>;
}) {
  const shots =
    task.script &&
    typeof task.script === "object" &&
    "shots" in task.script &&
    Array.isArray((task.script as { shots: unknown[] }).shots)
      ? (task.script as { shots: unknown[] }).shots.length
      : undefined;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 text-muted-foreground">
        <div>
          <span className="text-foreground">类型:</span> 漫剧
        </div>
        <div>
          <span className="text-foreground">分镜数:</span> {shots ?? "—"}
        </div>
        <div>
          <span className="text-foreground">总时长:</span> {task.durationSec ?? "—"}s
        </div>
        <div>
          {/* P0.6 车 B(2026-09-15):同上——settle 有台账时是累计真实扣费(含重出),
          没 settle 时退回估算值,不再是"重渲即覆盖丢历史"的旧字段,故不再标"本次"。 */}
          <span className="text-foreground">本单已消耗积分:</span>{" "}
          {task.status === "SUCCESS"
            ? (task.creditsUsed ?? 0)
            : task.creditsUsed && task.creditsUsed > 0
              ? `${task.creditsUsed}(进行中)`
              : "完成后结算"}
        </div>
      </div>
      {stripSystemPromptLines(task.promptFinal) && (
        <div className="mt-3">
          <div className="text-foreground font-medium mb-1">分镜脚本</div>
          <pre className="whitespace-pre-wrap text-xs bg-muted p-2 rounded text-muted-foreground max-h-60 overflow-y-auto">
            {stripSystemPromptLines(task.promptFinal)}
          </pre>
        </div>
      )}
      {task.characters && task.characters.length > 0 && (
        <div>
          <div className="text-foreground font-medium mb-2">参演角色</div>
          <div className="flex gap-2 flex-wrap">
            {task.characters.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1"
              >
                {c.thumbUrl && (
                  <img src={c.thumbUrl} alt={c.name} className="w-8 h-8 rounded object-cover" />
                )}
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
